import { useState } from 'react'
import { usePrompt } from '@ur-solutions/react-components'
import { ApproveCommentModal } from './ApproveCommentModal'
import { useConfirm } from '@/utils/hooks'
import type { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import type { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import type { ApproveWorkPeriodNode } from './types.graphql'

type ApprovalSide = 'driver' | 'manager'

type SalaryConfiguration = ReturnType<typeof useSalaryModuleConfiguration>
type PatchWorkPeriod = ReturnType<
  typeof useWorkPeriodMigrations
>['patchWorkPeriod']

type UseWorkPeriodApprovalActionsArgs = {
  refetchAfterApproval: () => void
  patchWorkPeriod: PatchWorkPeriod
  salaryConfiguration: SalaryConfiguration
  commentRequiredDriverApproval: string
  commentRequiredManagerApproval: string
}

export function useWorkPeriodApprovalActions({
  refetchAfterApproval,
  patchWorkPeriod,
  salaryConfiguration,
  commentRequiredDriverApproval,
  commentRequiredManagerApproval,
}: UseWorkPeriodApprovalActionsArgs) {
  const addPrompt = usePrompt()
  const confirm = useConfirm()
  // Guards the bulk "approve all for week / month" actions against
  // double-submit: `patchWorkPeriod` exposes no loading flag, so without this
  // the buttons stay enabled through the prompt + N-way `Promise.all` and can
  // be fired again mid-flight (a second redundant mutation burst and a UI race
  // against the still-stale approvable set).
  const [isBulkApproving, setIsBulkApproving] = useState(false)

  function getApprovalConfig(
    workPeriod: ApproveWorkPeriodNode,
    side: ApprovalSide
  ) {
    if (side === 'driver') {
      return {
        approved: workPeriod.approvedByUser,
        comment: workPeriod.userComment,
        approvedKey: 'approvedByUser' as const,
        commentKey: 'userComment' as const,
        commentRequiredOnApproval:
          salaryConfiguration.requireCommentOnDriverApproval,
        commentRequiredError: commentRequiredDriverApproval,
      }
    }

    return {
      approved: workPeriod.approvedByManager,
      comment: workPeriod.managerComment,
      approvedKey: 'approvedByManager' as const,
      commentKey: 'managerComment' as const,
      commentRequiredOnApproval:
        salaryConfiguration.requireCommentOnManagerApproval,
      commentRequiredError: commentRequiredManagerApproval,
    }
  }

  async function openApprovalCommentFlow(
    workPeriod: ApproveWorkPeriodNode,
    side: ApprovalSide
  ) {
    const config = getApprovalConfig(workPeriod, side)
    const { data } = await addPrompt<{
      comment: string
      approved: boolean
    } | null>(resolve => (
      <ApproveCommentModal
        existingComment={config.comment}
        existingApproved={config.approved}
        commentRequiredOnApproval={config.commentRequiredOnApproval}
        commentRequiredError={config.commentRequiredError}
        onSubmit={resolve}
      />
    ))
    if (!data) return

    await patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          [config.approvedKey]: data.approved,
          [config.commentKey]: data.comment,
        },
      },
    })
    refetchAfterApproval()
  }

  async function toggleApproval(
    workPeriod: ApproveWorkPeriodNode,
    side: ApprovalSide
  ) {
    const config = getApprovalConfig(workPeriod, side)
    if (config.commentRequiredOnApproval) {
      await openApprovalCommentFlow(workPeriod, side)
      return
    }

    await patchWorkPeriod({
      variables: {
        id: workPeriod.id,
        input: {
          [config.approvedKey]: !config.approved,
        },
      },
    })
    refetchAfterApproval()
  }

  // Bulk manager approval for the "approve all for week / month" actions.
  // The caller pre-filters `workPeriods` to the ones the manager may approve
  // (see `workPeriodApprovalState`) and that are not yet manager-approved.
  // When the company requires a comment on manager approval we prompt once and
  // reuse that comment for every period still missing one; otherwise a plain
  // confirmation guards the bulk action.
  async function approveManyAsManager(
    workPeriods: ApproveWorkPeriodNode[],
    confirmMessage: string
  ) {
    if (workPeriods.length === 0 || isBulkApproving) return

    setIsBulkApproving(true)
    try {
      let sharedComment: string | null = null
      if (salaryConfiguration.requireCommentOnManagerApproval) {
        const { data } = await addPrompt<{
          comment: string
          approved: boolean
        } | null>(resolve => (
          <ApproveCommentModal
            existingComment=""
            existingApproved
            commentRequiredOnApproval
            commentRequiredError={commentRequiredManagerApproval}
            onSubmit={resolve}
          />
        ))
        if (!data || !data.approved) return
        sharedComment = data.comment
      } else {
        const { data } = await confirm(confirmMessage)
        if (!data) return
      }

      await Promise.all(
        workPeriods.map(workPeriod =>
          patchWorkPeriod({
            variables: {
              id: workPeriod.id,
              input: {
                approvedByManager: true,
                // Never overwrite an existing manager comment; only fill in the
                // required comment where the period has none.
                ...(sharedComment && !workPeriod.managerComment
                  ? { managerComment: sharedComment }
                  : {}),
              },
            },
          })
        )
      )
      refetchAfterApproval()
    } finally {
      setIsBulkApproving(false)
    }
  }

  return {
    openApprovalCommentFlow,
    toggleApproval,
    approveManyAsManager,
    isBulkApproving,
  }
}
